// 261005 E1-P3a — bảng Nhân vật / Bối cảnh CHUNG 2 tầng của chặng ②.
//   Tầng "Tên trong phim": ghi qua dịch vụ ③ (E3-P3a): đổi tên ⇒ modal XEM THỬ → áp (thay tên trên kịch bản đã viết, đẩy lại Chapters); khoá / xoá tên ⇒ áp thẳng. Không hạ duyệt.
//   Tầng "Khung xương": ghi qua PATCH outline (luôn hạ về nháp) — qua `onPatch` của SkeletonStage.
//   Tầng "Nhận dạng hình ảnh" (261012 E1-P3): mô tả ngoại hình cố định Film đọc — duyệt / sửa tay / Giữ / xoá / đề xuất lại + thanh đầu bảng "Tạo đề xuất cho mục còn thiếu".
//     Ghi qua endpoint riêng `PATCH visual-identity/entity` (CAS theo `baseVersion`, KHÔNG hạ duyệt khung xương); chạy AI qua `POST visual-identity` (hỏi ước giá trước). Dùng CHUNG cờ `working` với hai tầng trên.
//     Logic thuần: `visualIdentityView.ts`; trạng thái/hành động: `useVisualIdentity.ts`; vẽ: `VisualIdentityCell` / `VisualIdentityBar`. Tải summary lỗi KHÔNG ảnh hưởng hai tầng kia.
// Tự tải `GET localize` khi mount và mỗi khi `dataVersion` đổi. Chỉ được mount khi sub-tab hiện
// (SkeletonStage đặt `destroyOnHidden` cho hai sub-tab này) ⇒ mở ② ở Tổng quan: 0 request localize.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Checkbox, Input, Modal, Segmented, Select, Space, Switch, Table, Tag, Tooltip, Typography, message } from 'antd';
import Button from './WbButton';
import { adaptationApi } from '../../../services/api/adaptation';
import type { LocalizeOp, LocalizeView, OutlinePatchOp } from '../../../types/adaptation';
import LocalizeEditModal, { applyLocalizeOpsDirect } from './LocalizeEditModal';
import { editErrorText, nameOps, surnameInputError } from './localizeEditView';
import { splitCharacterIds, surnameGroupsCoNghia } from '../adaptationUtils';
import {
  buildEntityRows, effectiveFilter, entityCellState, EntityFilter, EntityKind, EntityRow, entityFilter,
  localizeHasRun, localizeStatusLine, locationTierLabel, structureConfirm, StructureAction, StructureCtx,
} from './entityTableView';
import type { DangerCopyView } from './workbenchView';
import { newEntityIds } from './appendRunView';
import { useWorkbenchCtx } from './WorkbenchContext';
import VisualIdentityBar from './VisualIdentityBar';
import VisualIdentityCell from './VisualIdentityCell';
import { useVisualIdentity } from './useVisualIdentity';
import type { VisualCtx } from './visualIdentityView';

const { Text } = Typography;

export interface EntityFocus { id?: string; filter?: 'missing'; n: number }

interface Props {
  kind: EntityKind;
  focus?: EntityFocus | null;
  onFocusConsumed?: () => void;
  /** `patch` của SkeletonStage — chặn khi khoá, gọi API, `refreshAll`, báo kết quả. */
  onPatch: (ops: OutlinePatchOp[]) => Promise<void>;
  /** Cờ ghi DÙNG CHUNG với SkeletonStage (chặn ghi chồng tên ↔ khung xương). */
  working: boolean;
  setWorking: (v: boolean) => void;
  confirmCopy: (c: DangerCopyView, run: () => void | Promise<void>) => void;
}

const GENDER_LABEL: Record<string, string> = { male: 'nam', female: 'nữ', unknown: '?' };
const TIER_LABEL: Record<string, string> = { lead: 'Chính', support: 'Phụ', functional: 'Chức năng' };
const TIER_OPTIONS = [{ value: 'lead', label: 'Chính' }, { value: 'support', label: 'Phụ' }, { value: 'functional', label: 'Chức năng' }];
const LOC_OPTIONS = [{ value: 'primary', label: 'Chính' }, { value: 'other', label: 'Phụ' }];
const aliasTag: React.CSSProperties = { fontSize: 10, lineHeight: '16px', margin: 0, padding: '0 4px' };

const EntityTable: React.FC<Props> = ({ kind, focus, onFocusConsumed, onPatch, working, setWorking, confirmCopy }) => {
  const { storyId, data, busy: shellBusy, stageState, refreshAll, dataVersion, workbench } = useWorkbenchCtx();
  // 261005 E4-P4b: nhãn "Mới" cho thực thể do phần mới thêm (chỉ ở STOP 1).
  const newIds = useMemo(() => newEntityIds(workbench?.appendRun, kind), [workbench?.appendRun, kind]);
  const [view, setView] = useState<LocalizeView | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState<EntityFilter>('all');
  const currentStoryIdRef = useRef(storyId);
  const inflight = useRef(false);
  /** E3-P3a: lệnh đang chờ xem thử trong modal + chặn lưu lặp do blur khi modal đóng/mở. */
  const [pending, setPending] = useState<{ ops: LocalizeOp[]; title: string; rowId?: string } | null>(null);
  const suppressUntil = useRef(0);
  const [family, setFamily] = useState<{ ids: string[]; members: string[]; surname: string } | null>(null);
  currentStoryIdRef.current = storyId;

  // Tải lại theo `dataVersion` (mọi ghi đều `await refreshAll()` ⇒ không gọi load thủ công). Bỏ kết quả về muộn khi đổi truyện.
  useEffect(() => {
    let alive = true;
    const forStory = storyId;
    adaptationApi.getLocalize(forStory)
      .then((v) => { if (alive && currentStoryIdRef.current === forStory) { setView(v); setLoadFailed(false); } })
      .catch(() => { if (alive && currentStoryIdRef.current === forStory) setLoadFailed(true); });
    return () => { alive = false; };
  }, [storyId, dataVersion, retry]);
  useEffect(() => { setView(null); setDrafts({}); setFilter('all'); }, [storyId]);

  const outline = data.record?.outline ?? null;
  const approved = data.record?.status === 'APPROVED';
  const viewLoaded = view !== null && !loadFailed;
  const hasRun = localizeHasRun(view);
  const stageLocked = stageState('skeleton') === 'locked';
  const ctx = { stageLocked, busy: shellBusy || working, approved, hasRun, viewLoaded };

  const rows = useMemo(() => buildEntityRows(kind, outline, view), [kind, outline, view]);
  // 261012 E1-P3: nhận dạng hình ảnh — summary riêng, dùng CHUNG `working`/`setWorking` + `confirmCopy`.
  const visual = useVisualIdentity({ setWorking, confirmCopy });
  const visualCtx: VisualCtx = {
    stageLocked, working, jobRunning: visual.jobRunning, shellBusy, loaded: visual.summary !== null, loadFailed: visual.loadFailed,
  };
  const { viewOf: visualViewOf } = visual;
  const visualViews = useMemo(() => rows.map((r) => visualViewOf(kind, r.id)), [rows, visualViewOf, kind]);
  const missing = entityFilter(rows, 'all').missing;
  const activeFilter = effectiveFilter(filter, hasRun, missing);
  const shown = entityFilter(rows, activeFilter).rows;
  const nhomCoNghia = useMemo(() => surnameGroupsCoNghia(view), [view]);
  const splitIds = useMemo(() => splitCharacterIds(view), [view]);
  const status = viewLoaded ? localizeStatusLine({ localizeEnabled: view!.localizeEnabled !== false, hasRun, resultStale: view!.resultStale }) : null;

  // focusEntities: áp bộ lọc SAU khi view tải xong (hoặc tải lỗi), rồi báo đã dùng.
  useEffect(() => {
    if (!focus) return;
    if (focus.filter === 'missing' && !viewLoaded && !loadFailed) return;
    if (focus.filter === 'missing') setFilter('missing');
    onFocusConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus, viewLoaded, loadFailed]);

  /** Áp thẳng (không modal) lệnh không ảnh hưởng kịch bản: khoá, xoá tên. Kiểm lại ô còn bật, bật cờ dùng chung, `await refreshAll()`. */
  const applyDirect = useCallback(async (row: EntityRow, ops: LocalizeOp[], okMsg?: string, which: 'name' | 'keep' = 'name') => {
    if (inflight.current) return;
    const st = entityCellState(row, ctx)[which];
    if (!st.enabled) return;
    inflight.current = true;
    setWorking(true);
    try {
      await applyLocalizeOpsDirect(storyId, ops);
      await refreshAll();
      if (okMsg) message.success(okMsg);
    } catch (e: any) {
      const d = editErrorText(e);
      message.error(d.text, 6);
      if (d.refresh) await refreshAll();
    } finally {
      setDrafts((d) => { const { [row.id]: _x, ...rest } = d; return rest; });
      inflight.current = false;
      setWorking(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyId, kind, ctx.stageLocked, ctx.busy, ctx.hasRun, ctx.viewLoaded, refreshAll, setWorking]);

  const dropDraft = (id: string) => setDrafts((d) => { const { [id]: _x, ...rest } = d; return rest; });

  const saveName = (row: EntityRow) => {
    if (pending || Date.now() < suppressUntil.current) return;   // blur do modal bật/tắt, hoặc Enter rồi blur
    const next = drafts[row.id] ?? row.localizedName;
    const ops = nameOps(kind, row.id, row.localizedName, next);
    if (!ops) { dropDraft(row.id); return; }
    if (!entityCellState(row, ctx).name.enabled) { dropDraft(row.id); return; }
    if (ops[0].op === 'clearName') { applyDirect(row, ops, 'Đã xoá tên — lượt chạy sau sẽ đặt lại'); return; }
    suppressUntil.current = Date.now() + 600;   // Enter rồi blur trước khi state kịp cập nhật
    setPending({ ops, title: `Đổi tên "${row.localizedName || row.name}" → "${next.trim()}"`, rowId: row.id });
  };

  const closePending = () => {
    suppressUntil.current = Date.now() + 600;
    if (pending?.rowId) dropDraft(pending.rowId);
    setPending(null);
  };

  const openFamily = (row: EntityRow) => {
    const g = row.localizeMeta?.surnameGroup;
    const ids = rows.filter((x) => g && x.localizeMeta?.surnameGroup === g).map((x) => x.id);
    setFamily({ ids, members: ids, surname: '' });
  };
  const submitFamily = () => {
    if (!family) return;
    const err = surnameInputError(family.surname);
    if (err) { message.warning(err); return; }
    if (!family.members.length) { message.warning('Chọn ít nhất một thành viên'); return; }
    const ops: LocalizeOp[] = [{ op: 'renameSurname', characterIds: family.members, newSurname: family.surname.trim() }];
    setFamily(null);
    setPending({ ops, title: `Đổi họ cả nhà → "${family.surname.trim()}"` });
  };

  const guardedPatch = (row: EntityRow, action: StructureAction, extra: Partial<StructureCtx>, ops: OutlinePatchOp[]) => {
    if (!entityCellState(row, ctx).structure.enabled) return;
    const c = structureConfirm(action, { approved, name: row.name, isLocation: kind === 'location', ...extra });
    if (!c) { onPatch(ops); return; }
    confirmCopy(c, () => onPatch(ops));
  };

  const nameLabel = (r: EntityRow) => (r.localizedName ? `${r.localizedName} (${r.name})` : r.name);

  const identityCol = {
    title: 'Tên trong truyện', key: 'identity', width: 220, fixed: 'left' as const,
    render: (_: unknown, r: EntityRow) => {
      const tip = [r.role, r.from.length ? `Nguồn gốc: ${r.from.join(' · ')}` : '', kind === 'location' && r.reusePlan ? `Tái dùng: ${r.reusePlan}` : '']
        .filter(Boolean).join(' — ');
      return (
        <Space direction="vertical" size={0} style={{ width: '100%' }}>
          <Tooltip title={tip || undefined}><Text strong>{r.name}{newIds.has(r.id) && <Tag color="gold" style={{ marginLeft: 6 }}>Mới</Tag>}</Text></Tooltip>
          {kind === 'character' && r.role && <Text type="secondary" ellipsis={{ tooltip: r.role }} style={{ fontSize: 13, maxWidth: 200 }}>{r.role}</Text>}
          {kind === 'location' && r.reusePlan && <Text type="secondary" ellipsis={{ tooltip: r.reusePlan }} style={{ fontSize: 13, maxWidth: 200 }}>{r.reusePlan}</Text>}
          {kind === 'character' && r.aliases.length > 0 && (
            <Text type="secondary" style={{ fontSize: 13 }}>
              Cũng gọi: {r.aliases.slice(0, 4).join(' · ')}{r.aliases.length > 4 ? '…' : ''}
            </Text>
          )}
          {!!r.sourceAliases.length && (
            <Tooltip title="Nguyên tác gọi thực thể này bằng những chữ này">
              <Space size={2} wrap style={{ marginTop: 2 }}>
                {r.sourceAliases.slice(0, 4).map((a) => <Tag key={a} style={aliasTag}>{a}</Tag>)}
                {r.sourceAliases.length > 4 && <Text type="secondary" style={{ fontSize: 10 }}>+{r.sourceAliases.length - 4}</Text>}
              </Space>
            </Tooltip>
          )}
        </Space>
      );
    },
  };

  const nameGroup = {
    title: <Tooltip title="Sửa ở đây không cần duyệt lại khung xương"><span>Tên trong phim</span></Tooltip>,
    key: 'g-name',
    children: [
      {
        title: 'Tên trong phim', key: 'name', width: 260,
        render: (_: unknown, r: EntityRow) => {
          const st = entityCellState(r, ctx).name;
          const split = kind === 'character' && splitIds.has(r.id);
          if (!viewLoaded) {
            return loadFailed
              ? <Space size={4}><Text type="secondary">Không tải được tên</Text><Button size="small" type="link" onClick={() => setRetry((n) => n + 1)}>Thử lại</Button></Space>
              : <Text type="secondary">…</Text>;
          }
          const doi = (r.localizeMeta?.aliasMap ?? []).filter((m) => m.target && m.target !== m.source);
          const input = (
            <Input
              size="small" placeholder={st.placeholder} disabled={!st.enabled}
              status={r.missingName ? 'warning' : undefined}
              value={drafts[r.id] ?? r.localizedName}
              onChange={(e) => setDrafts((d) => ({ ...d, [r.id]: e.target.value }))}
              onBlur={() => saveName(r)} onPressEnter={() => saveName(r)}
              style={split ? { background: 'var(--wb-sel)' } : undefined}
            />
          );
          const body = (
            <Space direction="vertical" size={2} style={{ width: '100%' }}>
              <Tooltip title={st.enabled ? undefined : st.reason}>{input}</Tooltip>
              {doi.length > 0 && (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {doi.slice(0, 3).map((m) => `${m.source} → ${m.target}`).join(' · ')}{doi.length > 3 ? ` · +${doi.length - 3}` : ''}
                </Text>
              )}
              <Space size={4} wrap>
                {kind === 'character' && r.localizeMeta?.title && <Tag color="blue">{r.localizeMeta.title}</Tag>}
                {kind === 'character' && r.localizeMeta?.surnameGroup && nhomCoNghia.has(r.localizeMeta.surnameGroup) && <Tag color="purple">{r.localizeMeta.surnameGroup}</Tag>}
                {kind === 'character' && r.localizeMeta?.gender && <Tag>{GENDER_LABEL[r.localizeMeta.gender] ?? r.localizeMeta.gender}</Tag>}
                {kind === 'location' && r.localizeMeta?.tier && <Tag>{locationTierLabel(r.localizeMeta.tier)}</Tag>}
                {kind === 'character' && r.localizeMeta?.surnameGroup && nhomCoNghia.has(r.localizeMeta.surnameGroup) && (
                  <Button size="small" type="link" style={{ padding: 0, height: 18, fontSize: 11 }} disabled={!st.enabled} onClick={() => openFamily(r)}>Đổi họ cả nhà</Button>
                )}
              </Space>
            </Space>
          );
          return split ? <Tooltip title="Nhóm họ này nên cùng họ — kiểm lại">{body}</Tooltip> : body;
        },
      },
      {
        title: 'Giữ tên', key: 'keep', width: 90,
        render: (_: unknown, r: EntityRow) => {
          const st = entityCellState(r, ctx).keep;
          return (
            <Tooltip title={st.enabled || !st.reason ? 'Lượt bản địa hoá sau không đổi tên này. Gõ tên tay sẽ tự bật.' : st.reason}>
              <span>
                <Switch
                  size="small" checked={!!r.localizeMeta?.locked} disabled={!st.enabled}
                  onChange={(locked) => applyDirect(r, [{ op: 'setLock', kind, id: r.id, locked }], undefined, 'keep')}
                />
              </span>
            </Tooltip>
          );
        },
      },
    ],
  };

  const removeAction: StructureAction = 'remove';
  const structureChildren = kind === 'character'
    ? [
      {
        title: 'Hạng', key: 'tier', width: 130,
        render: (_: unknown, r: EntityRow) => (
          <Select
            size="small" value={r.tier} style={{ width: 110 }} options={TIER_OPTIONS}
            disabled={!entityCellState(r, ctx).structure.enabled}
            onChange={(tier) => guardedPatch(r, 'setTier', { newLabel: TIER_LABEL[tier as string] }, [{ op: 'setTier', id: r.id, tier: tier as any }])}
          />
        ),
      },
      {
        title: 'Gộp vào', key: 'merge', width: 160,
        render: (_: unknown, r: EntityRow) => (
          <Select
            size="small" placeholder="Gộp vào…" style={{ width: 140 }} value={null as any}
            disabled={!entityCellState(r, ctx).structure.enabled}
            options={rows.filter((x) => x.id !== r.id).map((x) => ({ value: x.id, label: nameLabel(x) }))}
            onChange={(target) => guardedPatch(r, 'merge', { dropLocalizedName: r.localizedName }, [{ op: 'mergeCharacters', id: String(target), fromId: r.id }])}
          />
        ),
      },
      {
        title: 'Bỏ', key: 'remove', width: 70,
        render: (_: unknown, r: EntityRow) => (
          <Button size="small" danger disabled={!entityCellState(r, ctx).structure.enabled}
            onClick={() => guardedPatch(r, removeAction, {}, [{ op: 'removeCharacter', id: r.id }])}>Bỏ</Button>
        ),
      },
    ]
    : [
      {
        title: 'Loại', key: 'primary', width: 120,
        render: (_: unknown, r: EntityRow) => (
          <Select
            size="small" value={r.primary ? 'primary' : 'other'} style={{ width: 100 }} options={LOC_OPTIONS}
            disabled={!entityCellState(r, ctx).structure.enabled}
            onChange={(k) => guardedPatch(r, 'setPrimary', { newLabel: k === 'primary' ? 'Chính' : 'Phụ' }, [{ op: 'setPrimary', id: r.id, primary: k === 'primary' }])}
          />
        ),
      },
      {
        title: 'Bỏ', key: 'remove', width: 70,
        render: (_: unknown, r: EntityRow) => (
          <Button size="small" danger disabled={!entityCellState(r, ctx).structure.enabled}
            onClick={() => guardedPatch(r, removeAction, {}, [{ op: 'removeLocation', id: r.id }])}>Bỏ</Button>
        ),
      },
    ];
  const structureGroup = {
    title: <Tooltip title="Sửa ở đây đưa khung xương về bản nháp, phải duyệt lại"><span>Khung xương</span></Tooltip>,
    key: 'g-structure', children: structureChildren,
  };

  const visualGroup = {
    title: <Tooltip title="Mô tả ngoại hình cố định (tiếng Anh) — thứ Film đọc. Sửa ở đây không hạ duyệt khung xương"><span>Nhận dạng hình ảnh</span></Tooltip>,
    key: 'g-visual',
    children: [
      {
        title: 'Mô tả cố định (tiếng Anh) · thứ Film đọc', key: 'visual', width: 560,
        render: (_: unknown, r: EntityRow) => (
          <VisualIdentityCell
            kind={kind} id={r.id} label={r.localizedName || r.name} view={visualViewOf(kind, r.id)} ctx={visualCtx} api={visual}
          />
        ),
      },
    ],
  };

  return (
    <div id="section-entities">
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        {status && <Alert type={status.tone} showIcon message={status.text} />}
        <VisualIdentityBar views={visualViews} ctx={visualCtx} api={visual} />
        {viewLoaded && hasRun && missing > 0 && (
          <Segmented
            value={activeFilter}
            onChange={(v) => setFilter(v as EntityFilter)}
            options={[{ value: 'all', label: `Tất cả (${rows.length})` }, { value: 'missing', label: `Chưa có tên (${missing})` }]}
          />
        )}
        <Table
          rowKey="id" size="small" pagination={false} dataSource={shown}
          columns={[identityCol, nameGroup, structureGroup, visualGroup] as any}
          scroll={{ x: 'max-content' }}
          onRow={(r: EntityRow) => ({ id: `outline-ref-${r.id}` })}
        />
      </Space>
      <LocalizeEditModal
        storyId={storyId} ops={pending?.ops ?? null} title={pending?.title ?? ''} screenModifiedAt={data.record?.modifiedAt ?? null}
        onClose={closePending} onApplied={() => { refreshAll(); }} onStale={() => { refreshAll(); }}
      />
      <Modal
        open={!!family} title="Đổi họ cả nhà" okText="Xem thử" cancelText="Thôi" onCancel={() => setFamily(null)} onOk={submitFamily} destroyOnClose
      >
        {family && (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Text type="secondary" style={{ fontSize: 13 }}>Chọn thành viên đổi họ (mặc định cả nhóm cùng họ). Họ mới là MỘT từ.</Text>
            <Checkbox.Group
              value={family.members} onChange={(v) => setFamily((f) => (f ? { ...f, members: v as string[] } : f))}
              options={family.ids.map((id) => ({ value: id, label: nameLabel(rows.find((x) => x.id === id) as EntityRow) }))}
              style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
            />
            <Input placeholder="Họ mới" value={family.surname} onChange={(e) => setFamily((f) => (f ? { ...f, surname: e.target.value } : f))} onPressEnter={submitFamily} />
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default EntityTable;
